import { useContext, useEffect, useState, createRef, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLingui } from '@lingui/react';
import { useCookies } from 'react-cookie';
import { translate } from '../../Service/PlaceholderTranslationService';
import { getClosest } from '../../Utils/utils';
import { FocusedNodeContext } from '../../Service/Context/FocusedNode';
import TextSelectionPopover from './TextSelectionPopover';
import { useMantineTheme } from '@mantine/core';

const PlaceholderInputField = ({
  htmlString: initialHtmlString,
  selectedValue,
  autoFocus,
  inlinePlaceholder,
  className,
  style,
  loading,
  big,
  error,
  name,
  finish,
  disabled,
  settings,
  onKeyDown,
  onInputChange,
  onBlur,
  onFocus,
  disableTextSelectionPopover,
  omitStylesOnPaste = false,
  ...rest
}) => {
  const [ref, setRef] = useState(createRef());
  const lastBlurHtmlRef = useRef(null);
  const { setFocusedNode, setListPosition } = useContext(FocusedNodeContext);
  const { colorScheme, colors } = useMantineTheme();

  const { i18n } = useLingui();
  const [locale, setLocale, removeLocale] = useCookies(['locale']);

  const getItems = () =>
    translate.toLocale(
      initialHtmlString,
      true,
      true,
      settings,
      i18n,
      locale,
      () => finish(ref.current.innerHTML),
      rest,
    );

  function focusNode(e) {
    const parentNode = getClosest(e.target, '.form-control');
    setFocusedNode(parentNode);
    setListPosition(rest.index);
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }

  function handleBlur(e) {
    if (typeof onBlur === 'function') {
      onBlur(e);
      return;
    }

    if (typeof finish !== 'function') {
      return;
    }

    const newHtmlString = e.currentTarget.innerHTML;
    if (lastBlurHtmlRef.current === newHtmlString) {
      return;
    }

    lastBlurHtmlRef.current = newHtmlString;
    finish(newHtmlString);
  }

  const styles = {
    error: {
      borderColor: 'red',
    },
    input: {
      marginBottom: 0,
      height: '100%',
      minHeight: big ? 150 : 34,
      MozAppearance: 'textfield',
      WebkitAppearance: 'textfield',
      display: 'inline-block',
      backgroundColor: disabled ? '#f1f1f1' : colorScheme === 'dark' ? colors.dark[6] : 'white',
      color: colorScheme === 'dark' ? '#fff' : '#000',
      ...style,
    },
    label: {
      // color: 'red',
      fontWeight: 800,
    },
    inputContainer: {
      marginBottom: 9,
    },
    inputAndPlaceholderContainer: {
      display: 'flex',
    },
  };

  {
    /* fixes a bug in Firefox, that the placeholder cannot be deleted by backspace */
  }

  function onKeydownInFF(e) {
    /* if (e.key === "Backspace") {
            let selection = document.getSelection();

            // if caret is at the begining of the text node (0), remove previous element
            if (selection && selection.anchorOffset === 0) {
                if (selection.anchorNode.previousSibling != null && selection.anchorNode.previousSibling.parentNode != null) {
                    selection.anchorNode.previousSibling.parentNode.removeChild(selection.anchorNode.previousSibling)
                }
            }
        } */
  }

  function removeInlineStylesFromHtml(htmlString) {
    const template = document.createElement('template');
    template.innerHTML = htmlString;
    template.content.querySelectorAll('[style]').forEach((node) => node.removeAttribute('style'));
    template.content.querySelectorAll('style').forEach((node) => node.remove());
    return template.innerHTML;
  }

  function handlePaste(e) {
    if (!omitStylesOnPaste) {
      return;
    }

    const html = e.clipboardData.getData('text/html');
    if (!html) {
      return;
    }

    e.preventDefault();
    window.document.execCommand('insertHTML', false, removeInlineStylesFromHtml(html));
  }

  let classes = loading ? `loading form-control ${className}` : `form-control ${className}`;
  if (disabled) {
    classes += ' disabled';
  }

  const items = getItems();

  return (
    <>
      {!disableTextSelectionPopover && <TextSelectionPopover finish={finish} ref={ref} />}
      <div
        ref={(el) => el && el != null && setRef(el)}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onFocus={focusNode}
        onInput={onInputChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        placeholder={i18n._(inlinePlaceholder || '')}
        tabIndex={0}
        name={name}
        style={error ? { ...styles.error, ...styles.input } : styles.input}
        className={classes}
        onKeyDown={(e) => {
          // credit: https://stackoverflow.com/a/25397485/104380
          const isFF = !!navigator.userAgent.match(/firefox/i);
          const editableElm = document.querySelector('div');

          // listen to any key press
          if (isFF) editableElm.addEventListener('keydown', onKeydownInFF);

          if (typeof onKeyDown === 'function') {
            onKeyDown(e);
          } else if (e.keyCode === 13 && e.shiftKey) {
          } else if (e.keyCode === 13) {
            // block enter
            e.preventDefault();
            window.document.execCommand('insertHTML', false, '<br><br>');
          }
        }}
      >
        {items}
      </div>
    </>
  );
};

PlaceholderInputField.propTypes = {
  htmlString: PropTypes.string,
  omitStylesOnPaste: PropTypes.bool,
};

PlaceholderInputField.defaultProps = {
  omitStylesOnPaste: false,
};

export default PlaceholderInputField;
